import {
  Box,
  IconButton,
  Stack,
  TablePagination,
  Typography,
} from '@mui/material';
import { blueGrey } from '@mui/material/colors';
import type { AverageMargin } from 'api/managementReportApi';
import React, { useMemo } from 'react';
import { getMarginSummaryCategoryLabel } from './marginSummaryLabels';
import MarginSummaryMatrixTable from './MarginSummaryMatrixTable';
import {
  getCombinationKey,
  sortCombinationsDefaultFirst,
} from './marginSummaryTableUtils';
import { Refresh } from '@mui/icons-material';
import { useQueryClient } from '@tanstack/react-query';

export type MarginSummaryCategoryPanelProps = {
  categorySummary: AverageMargin;
  page: number;
  rowsPerPage: number;
  onPageChange: (newPage: number) => void;
  onRowsPerPageChange: (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void;
};

const MarginSummaryCategoryPanel = ({
  categorySummary,
  page,
  rowsPerPage,
  onPageChange,
  onRowsPerPageChange,
}: MarginSummaryCategoryPanelProps) => {
  const {
    allCombinations,
    marginByNameAndCombination,
    itemNames,
    paginatedItemNames,
  } = useMemo(() => {
    const allCombinations = sortCombinationsDefaultFirst(
      Array.from(
        new Set(
          categorySummary.details.map((detail) =>
            getCombinationKey(detail.branch, detail.client)
          )
        )
      )
    );

    const marginByNameAndCombination = categorySummary.details.reduce(
      (acc, detail) => {
        if (!acc[detail.name]) {
          acc[detail.name] = {};
        }
        const combinationKey = getCombinationKey(detail.branch, detail.client);
        acc[detail.name][combinationKey] = detail.margin;
        return acc;
      },
      {} as Record<string, Record<string, number | null>>
    );

    const itemNames = Array.from(
      new Set(categorySummary.details.map((detail) => detail.name))
    );
    const paginatedItemNames = itemNames.slice(
      page * rowsPerPage,
      page * rowsPerPage + rowsPerPage
    );

    return {
      allCombinations,
      marginByNameAndCombination,
      itemNames,
      paginatedItemNames,
    };
  }, [categorySummary, page, rowsPerPage]);

  const queryClient = useQueryClient();

  const onRefreshAction = () => {
    void queryClient.invalidateQueries(['margin-summary']);
  };

  return (
    <Box
      key={categorySummary.category}
      sx={{
        backgroundColor: '#fff',
        borderRadius: 1,
        mb: 4,
        overflow: 'hidden',
      }}>
      <Stack
        direction="row"
        sx={{
          justifyContent: 'space-between',
          p: '1rem',
          borderBottom: '1px solid',
          borderColor: blueGrey[50],
        }}>
        <Typography variant="h6" sx={{ color: blueGrey[700] }}>
          {getMarginSummaryCategoryLabel(categorySummary.category)} Margin
          Summary
        </Typography>

        <IconButton
          size="small"
          color="primary"
          onClick={() => {
            onRefreshAction();
          }}>
          <Refresh fontSize="small" />
        </IconButton>
      </Stack>

      <Box sx={{ p: 2 }}>
        <MarginSummaryMatrixTable
          category={categorySummary.category}
          allCombinations={allCombinations}
          paginatedNames={paginatedItemNames}
          totalItemCount={itemNames.length}
          marginByNameAndCombination={marginByNameAndCombination}
        />
        {categorySummary.category !== 'labour' &&
          categorySummary.category !== 'otherExpenses' && (
            <TablePagination
              rowsPerPageOptions={[5, 10, 25, 50]}
              component="div"
              count={itemNames.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={(_event, newPage) => onPageChange(newPage)}
              onRowsPerPageChange={onRowsPerPageChange}
            />
          )}
      </Box>
    </Box>
  );
};

export default MarginSummaryCategoryPanel;
